import { genderOptions, genderOptionsWithNeutral } from '@common/constants';
import { getGenderOptionsForRestriction } from '@common/helpers/genderRestriction';
import { useTranslations } from '@lang';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { useCallback, useMemo } from 'react';
import { FieldErrors, UseFormReturn } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import type { ExistingGuest, GuestConfig, OptionsPanelConfig } from 'voyager';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import {
  buildInitialConfigurationGroups,
  ConfigGroupErrors,
  mapConfigGroupToOptionsPanel,
  shouldRenderConfigGroup,
  type OptionsPanelLabels,
} from '../SpaDayBooking.helpers';

type GuestFormErrors = FieldErrors<SpaPackageFormValues['guests'][number]>;

type UseGuestConfigParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  watchedGuests: SpaPackageFormValues['guests'] | undefined;
  configGroupsNoExtras: ConfigurationGroupType[];
  showGenderNeutral: boolean;
  genderSpecific: 'M' | 'F' | null;
  existingGuests: ExistingGuest[];
  guestsErrors?: GuestFormErrors[];
  requireTimeslots: boolean;
};

export function useGuestConfig({
  form,
  watchedGuests,
  configGroupsNoExtras,
  showGenderNeutral,
  genderSpecific,
  existingGuests,
  guestsErrors,
  requireTimeslots,
}: UseGuestConfigParams) {
  const { t } = useTranslations();

  const titleOptions = useMemo(
    () => [...maleTitlesOptions, ...femaleTitlesOptions].map((o) => t(o.label)),
    [t]
  );

  const genderOpts = useMemo(() => {
    const opts = showGenderNeutral ? genderOptionsWithNeutral : genderOptions;
    const translated = opts.map((o) => ({ value: o.value, label: t(o.id) }));
    return getGenderOptionsForRestriction(genderSpecific, translated).genderOpts;
  }, [showGenderNeutral, genderSpecific, t]);

  const titleValueToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    [...maleTitlesOptions, ...femaleTitlesOptions].forEach((o) => {
      map[o.value] = t(o.label);
    });
    return map;
  }, [t]);

  const titleLabelToValue = useMemo(() => {
    const map: Record<string, string> = {};
    [...maleTitlesOptions, ...femaleTitlesOptions].forEach((o) => {
      map[t(o.label)] = o.value;
    });
    return map;
  }, [t]);

  const optionsPanelLabels = useMemo<OptionsPanelLabels>(
    () => ({
      includedTitle: t('spa.booking.included.title'),
      includedSubtitle: t('spa.booking.included.subtitle'),
      optionsTitle: t('spa.booking.options.title'),
      optionsSubtitle: t('spa.booking.options.subtitle'),
      optionsSubtitleMultiple: t('spa.booking.options.subtitleMultiple'),
      formatSelectionCount: (selected, required) =>
        t('spa.booking.selectionCount', { selected, required }),
    }),
    [t]
  );

  const guestFieldLabels = useMemo(
    () => ({
      title: t('common.title'),
      firstName: t('common.firstName'),
      lastName: t('common.lastName'),
      gender: t('common.gender'),
    }),
    [t]
  );

  const translateGuestFieldErrors = useCallback(
    (guestErrors?: GuestFormErrors) => {
      if (!guestErrors) return undefined;
      const messageFor = (field: 'title' | 'firstName' | 'lastName' | 'gender', label: string) =>
        guestErrors[field] ? t('error.fieldRequired', { label }) : undefined;
      const translated = {
        title: messageFor('title', guestFieldLabels.title),
        firstName: messageFor('firstName', guestFieldLabels.firstName),
        lastName: messageFor('lastName', guestFieldLabels.lastName),
        gender: messageFor('gender', guestFieldLabels.gender),
      };
      const hasAny = Object.values(translated).some(Boolean);
      return hasAny ? translated : undefined;
    },
    [t, guestFieldLabels]
  );

  const panelErrorMessages = useMemo(
    () => ({
      timeslotRequired: t('error.fieldRequired', { label: t('spa.booking.timeslots.title') }),
    }),
    [t]
  );

  const guests: GuestConfig[] = useMemo(() => {
    return (watchedGuests || []).map((guest, idx) => {
      const guestConfigGroups = guest.configurationGroups;
      const guestErrors = guestsErrors?.[idx];
      const configGroupErrors =
        (guestErrors?.configurationGroups as unknown as
          | Record<string, ConfigGroupErrors>
          | undefined) || {};

      const optionsPanels: OptionsPanelConfig[] = configGroupsNoExtras
        .filter(shouldRenderConfigGroup)
        .map((cg) => {
          const groupErrors = configGroupErrors[`group_${cg.id}`];
          const min = cg.minimumSelections ?? 1;
          const optionRequired = t('treatment.pleaseSelectFollowingOptions', {
            action: t('treatment.selectAmount', { amount: min }),
          });
          return mapConfigGroupToOptionsPanel(
            cg,
            guestConfigGroups,
            optionsPanelLabels,
            groupErrors,
            {
              optionRequired,
              timeslotRequired: panelErrorMessages.timeslotRequired,
            },
            requireTimeslots
          );
        })
        .filter((p): p is OptionsPanelConfig => p !== null);

      const guestFormValues = {
        title: titleValueToLabel[guest.title] || guest.title || '',
        firstName: guest.firstName || '',
        lastName: guest.lastName || '',
        gender: guest.gender || '',
      };

      return {
        guestNumber: idx + 1,
        values: guestFormValues,
        existingGuests,
        selectedGuestId: guest.selectedGuestId,
        errors: translateGuestFieldErrors(guestErrors),
        optionsPanels,
      };
    });
  }, [
    watchedGuests,
    configGroupsNoExtras,
    titleValueToLabel,
    existingGuests,
    optionsPanelLabels,
    guestsErrors,
    panelErrorMessages,
    translateGuestFieldErrors,
    requireTimeslots,
    t,
  ]);

  const defaultGender = genderSpecific === 'M' ? 'male' : genderSpecific === 'F' ? 'female' : '';

  const handleGuestCountChange = useCallback(
    (count: number) => {
      const current = form.getValues('guests') || [];
      let nextGuests = current;
      if (count > current.length) {
        nextGuests = [
          ...current,
          ...Array.from({ length: count - current.length }, () => ({
            title: '',
            firstName: '',
            lastName: '',
            gender: defaultGender,
            configurationGroups: buildInitialConfigurationGroups(configGroupsNoExtras),
          })),
        ];
      } else if (count < current.length) {
        nextGuests = current.slice(0, count);
      }
      form.setValue('guests', nextGuests as SpaPackageFormValues['guests'], {
        shouldDirty: true,
      });
    },
    [form, defaultGender, configGroupsNoExtras]
  );

  const handleGuestChange = useCallback(
    (guestIndex: number, field: string, value: string) => {
      const currentGuests = form.getValues('guests') || [];
      const updatedGuests = currentGuests.map((guest, idx) => {
        if (idx !== guestIndex) return guest;
        if (field === 'title') {
          const actualValue = titleLabelToValue[value] || value;
          return { ...guest, title: actualValue };
        }
        return { ...guest, [field]: value };
      });
      form.setValue('guests', updatedGuests as SpaPackageFormValues['guests'], {
        shouldDirty: true,
      });
    },
    [form, titleLabelToValue]
  );

  const spaDaySetValueOptions = useMemo(() => ({ shouldDirty: true }), []);

  const handleExistingGuestSelect = useExistingGuestSelect({
    form,
    existingGuests,
    titleLabelToValue,
    defaultGender,
    setValueOptions: spaDaySetValueOptions,
  });

  return {
    titleOptions,
    genderOpts,
    guests,
    handleGuestCountChange,
    handleGuestChange,
    handleExistingGuestSelect,
  };
}
